<template>
  <div class="w-full h-full overflow-y-hidden border-l-[1px] border-gray-200 bg-[#f7f7f7] flex flex-col">
    <!-- Loading state -->
    <div
      v-show="loading"
      class="flex items-center justify-center h-full min-h-72"
      role="status"
      aria-busy="true"
    >
      <LoaderCircle class="animate-spin mr-2" />Loading...
    </div>

    <!-- No role found state -->
    <div
      v-show="!loading && noData"
      class="flex items-center justify-center h-full min-h-72"
    >
      <HeartCrack class="h-5 w-5 mr-2" />
      <span class="text-gray-600 text-sm">{{ emptyText }}</span>
    </div>

    <div
      v-if="!loading && !noData"
      :key="String(route.params.role_id)"
      class="w-full h-full bg-white flex flex-col fadeIn min-h-0"
    >
      <!-- Top section -->
      <div
        class="w-full flex justify-between items-center px-2 py-1 bg-[#f7f7f7] border-b-[1px] border-gray-200 flex-shrink-0"
      >
        <router-link
          v-if="backRoute"
          :to="backRoute"
          class="cursor-pointer text-sm aspect-square h-9 hover:bg-gray-200 transition-all duration-200 rounded-full flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cosmicPurple"
          :aria-label="backLabel"
          :title="backLabel"
        >
          <ChevronsLeft class="inline-block h-4 text-gray-400" />
        </router-link>

        <div class="ml-auto flex items-center gap-2">
          <slot name="header-actions" />
          <router-link
            v-if="(((isGuide || isFellow) && editRoute) || edit) && editRoute"
            :to="editRoute"
            class="ml-auto cursor-pointer text-xs py-1 px-3 rounded-md border-[1px] border-gray-200 hover:bg-white transition-all duration-200 hover:shadow-md hover:translate-y-[-1px] flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cosmicPurple"
            :aria-label="editLabel"
            :title="editLabel"
          >
            <Pencil class="w-3 h-3 inline-block" />
            {{ editLabel }}
          </router-link>
        </div>
      </div>

      <!-- Container for the card padding -->
      <div class="p-2 bg-lightGray flex-1 min-h-0 overflow-y-auto">
        <slot />
      </div>
    </div>

    <router-view></router-view>
  </div>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'
import type { PropType } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import {
  LoaderCircle,
  Pencil,
  ChevronsLeft,
  HeartCrack,
} from 'lucide-vue-next'
import { useAuth } from '@/composables/useAuth'

const props = defineProps({
  loading: Boolean,
  noData: Boolean,
  backRoute: {
    type: [String, Object] as PropType<RouteLocationRaw>,
    default: undefined
  },
  editRoute: {
    type: [String, Object] as PropType<RouteLocationRaw>,
    default: undefined
  },
  backLabel: {
    type: String,
    default: 'Back'
  },
  emptyText: {
    type: String,
    default: 'No job found'
  },
  editLabel: {
    type: String,
    default: 'Edit'
  },
  edit: {
    type: Boolean,
    default: false
  }
})

const { isGuide, isFellow } = useAuth()

const route = useRoute()
</script>

<style scoped>
.transition-opacity {
  transition-property: opacity;
}
</style>
